'use client';

import Image from 'next/image';
import Link from 'next-intl/link';
import {
  MdPersonAdd,
  MdVolunteerActivism,
  MdPerson,
  MdMenu,
  MdClose,
  MdAdd,
  MdStar,
  MdLogout,
  MdChatBubbleOutline,
  MdInfoOutline,
  MdPublic,
  MdGavel,
} from 'react-icons/md';
import { BiSolidFactory, BiSolidUserDetail } from 'react-icons/bi';
import { IoIosRibbon } from 'react-icons/io';
import { FaPhone } from 'react-icons/fa6';
import LoginModal from './userForms/LoginModal';
import ForgottenModal from './userForms/ForgottenPasswordModal';
import ResetPasswordModal from './userForms/ResetPasswordModal';
import ExtendedRegistrationModal from './userForms/ExtendedRegistrationModal';
import FirstLoginModal from './userForms/FirstLoginModal';
import SellFormModal from './userForms/SellFormModal';
import useParamsEditor from '@/src/utils/useParamsEditor';
import { useState, Fragment, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/src/redux/store';
import { userState } from '@/src/redux/userSlice/userSlice';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { useLocaleRoutes } from '../utils/useLocaleRoutes';
import { usePathname } from 'next-intl/client';
import AccountModal from './Account';
import CompanyRegistrationModal from './userForms/CompanyRegistrationModal';
import {
  customerState,
  customerTypes,
} from '../redux/customerSlice/customerSlice';
import { useLogoutHandler } from '../redux/userSlice/loginHandlers';
import { locales } from '../i18n';
import ComplaintFormModal from './userForms/ComplaintFormModal';
import getOption from '@/src/utils/getOption';
import { BsFillEnvelopeFill } from 'react-icons/bs';
import WebSocketComponent from '@api/websocket';
import ErrorBoundary from './ErrorBoundary';
import Fallback from './Fallback';
import Modal from './Modal';
import InfoConfirmationModal from './userForms/InfoConfirmationModal';
import { setCookie } from 'cookies-next';
import CategorySubNavbar from './CategoriesNavbar';
import { FaSearch } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import useLocaleSuffix from '../utils/useLocaleSuffix';

export default function NavBar() {
  const t = useTranslations('Navbar');
  const n = useTranslations('navigation');
  const path = useLocaleRoutes();
  const pathname = usePathname();
  const editParam = useParamsEditor();
  const searchParams = useSearchParams();
  const logout = useLogoutHandler();
  const locale = useLocale();
  const { handleSubmit: handleSearch, register } = useForm();
  const { push } = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const localeSuffix = useLocaleSuffix();
  const user: userState = useSelector((state: RootState) => {
    return state.user;
  });
  const customer: customerState = useSelector((state: RootState) => {
    return state.customer;
  });

  const openLogin = () => {
    document.body.classList.remove('nav-open');
    setMenuOpen(false);
    editParam('modal', 'login');
  };
  const openRegistration = () => {
    editParam('modal', 'register');
  };
  const closeMenu = () => {
    document.body.classList.remove('nav-open');
    setMenuOpen(false);
  };
  const logoutHandler = () => {
    document.body.classList.remove('nav-open');
    setMenuOpen(false);
    logout();
  };
  const toggleMenu = () => {
    if (menuOpen) {
      document.body.classList.remove('nav-open');
    } else {
      document.body.classList.add('nav-open');
    }
    setMenuOpen(!menuOpen);
  };

  const search = (data: any) => {
    const prefix =
      process.env.NEXT_PUBLIC_DEFAULT_LOCALE === locale ? '' : `/${locale}`;
    push(
      `${prefix}/${path('all')}?search=${data.search}&lang=${localeSuffix('', false)}`
    );
  };

  useEffect(() => {
    if (!searchParams.get('activateEditButton')) {
      return;
    }
    const expire = new Date();
    expire.setTime(new Date().getTime() + 1000 * 60 * 60 * 24 * 60);
    setCookie('adminEditButtons', '1', {
      expires: expire,
    });
  }, [searchParams]);

  return (
    <>
      {getOption('HAS_AUCTIONS') && <WebSocketComponent />}
      <header className='navbar sticky top-0 z-40 flex max-h-[100vh] w-full max-w-full flex-col overflow-auto border-b-2 border-white bg-dark lg:border-none'>
        <section className='border-b border-[#4B4B4B] lg:order-2'>
          <div className='mx-auto flex w-full max-w-[90rem] flex-grow flex-wrap items-center justify-between gap-6 px-4 py-4 md:px-10'>
            <a
              href={getOption('DEFAULT_LOCALE') === locale ? '/' : `/${locale}`}
              className='relative flex-none'
            >
              <Image
                src={
                  getOption('LOGO')
                    ? `/images/${getOption('LOGO')}`
                    : '/images/ats-icon.svg'
                }
                alt='ATS'
                width={0}
                height={0}
                priority
                className='h-[25px] w-auto md:h-[30px] lg:h-[40px]'
              />
            </a>
            <button
              className='order-1 block text-4xl sm:order-3 lg:hidden'
              onClick={toggleMenu}
            >
              <span className='sr-only'>{t('aria')}</span>
              {!menuOpen ? <MdMenu /> : <MdClose />}
            </button>
            <form
              onSubmit={handleSearch(search)}
              className='order-3 flex w-full md:order-2 md:w-auto lg:min-w-[28rem]'
            >
              <input
                {...register('search', {
                  required: true,
                  value: searchParams.get('search'),
                })}
                className='flex-grow rounded-l border border-r-0 border-[#444] bg-[#262626] px-2 py-1 text-white'
              ></input>
              <button className='flex items-center gap-2 border border-brand bg-brand px-4 py-1 font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'>
                <FaSearch /> {t('search')}
              </button>
            </form>
          </div>
        </section>
        <section
          className={`${
            menuOpen ? 'block' : 'hidden'
          } bg-[#1E1E1E] text-sm text-[#b2b2b2] lg:order-1 lg:block`}
        >
          <nav className='mx-auto w-full max-w-[90rem] p-4 lg:px-10 lg:py-1'>
            <ul className='flex w-full flex-col flex-wrap gap-4 text-xs font-bold lg:flex-row lg:items-center lg:gap-8 lg:text-sm'>
              <li>
                <Link
                  href={`/${path('customer-center')}`}
                  onClick={closeMenu}
                  className={`${
                    pathname === `/${path('customer-center')}`
                      ? 'underline'
                      : ''
                  } flex items-center underline-offset-2 hover:underline`}
                >
                  <span className='mr-2 text-[#4B4B4B]'>
                    <MdInfoOutline />
                  </span>
                  {t('customerCenter')}
                </Link>
              </li>
              <li>
                <Link
                  href={`/${path('contact')}`}
                  onClick={closeMenu}
                  className={`${
                    pathname === `/${path('contact')}` ? 'underline' : ''
                  } flex items-center underline-offset-2 hover:underline`}
                >
                  <span className='mr-2 text-[#4B4B4B]'>
                    <MdChatBubbleOutline />
                  </span>
                  {t('contact')}
                </Link>
              </li>
              <li className='flex items-center gap-2'>
                <span className='text-[#4B4B4B]'>
                  <MdPublic />
                </span>
                <ul className='flex gap-2'>
                  {locales.map((lang, index) => {
                    return (
                      <Fragment key={index}>
                        <li>
                          <Link
                            href={pathname}
                            onClick={closeMenu}
                            locale={lang}
                            className={`underline-offset-2 hover:underline ${
                              locale === lang ? 'underline' : ''
                            }`}
                          >
                            {t(lang)}
                          </Link>
                        </li>
                        {index < locales.length - 1 && <li>/</li>}
                      </Fragment>
                    );
                  })}
                </ul>
              </li>
              <li className='hidden flex-grow lg:block'></li>
              {!user.loggedIn && (
                <>
                  <li>
                    <Link
                      href={`/${path('why-ats')}`}
                      onClick={closeMenu}
                      className={`${
                        pathname === `/${path('why-ats')}` ? 'underline' : ''
                      } flex items-center underline-offset-8 hover:underline`}
                    >
                      <span className='mr-2 text-brand'>
                        <IoIosRibbon />
                      </span>
                      {n('whyATS')}
                    </Link>
                  </li>
                  {!getOption('SHOW_USER_FUNCTIONS') && (
                    <>
                      <li>
                        <Link
                          href={`/${path('new-ad')}`}
                          onClick={closeMenu}
                          className={`${
                            pathname === `/${path('new-ad')}` ? 'underline' : ''
                          } flex items-center underline-offset-8 hover:underline`}
                        >
                          <span className='mr-2 text-brand'>
                            <MdVolunteerActivism />
                          </span>
                          {t('sell')}
                        </Link>
                      </li>
                      {process.env.NEXT_PUBLIC_PHONE && (
                        <li>
                          <a
                            href={`tel:${String(process.env.NEXT_PUBLIC_PHONE).replaceAll(' ', '')}`}
                            className={`flex items-center underline-offset-8 hover:underline`}
                          >
                            <span className='mr-2 text-brand'>
                              <FaPhone />
                            </span>
                            {process.env.NEXT_PUBLIC_PHONE}
                          </a>
                        </li>
                      )}
                      {!process.env.NEXT_PUBLIC_PHONE &&
                        process.env.NEXT_PUBLIC_MAIL && (
                          <li>
                            <a
                              href={`mailto:${String(process.env.NEXT_PUBLIC_MAIL)}`}
                              className={`flex items-center underline-offset-8 hover:underline`}
                            >
                              <span className='mr-2 text-brand'>
                                <BsFillEnvelopeFill />
                              </span>
                              {process.env.NEXT_PUBLIC_MAIL}
                            </a>
                          </li>
                        )}
                    </>
                  )}
                  {getOption('SHOW_USER_FUNCTIONS') && (
                    <>
                      <li>
                        <button
                          onClick={() => {
                            openRegistration();
                            closeMenu();
                          }}
                          className='flex items-center underline-offset-8 hover:underline'
                        >
                          <span className='mr-2 text-brand'>
                            <MdPersonAdd />
                          </span>
                          {t('register')}
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            openLogin();
                            closeMenu();
                          }}
                          className='flex items-center underline-offset-8 hover:underline'
                        >
                          <span className='mr-2 text-brand'>
                            <MdPerson />
                          </span>
                          {t('login')}
                        </button>
                      </li>
                    </>
                  )}
                </>
              )}
              {user.loggedIn && (
                <>
                  <li>
                    <Link
                      href={`/${path('new-ad')}`}
                      onClick={closeMenu}
                      className={`${
                        pathname === `/${path('new-ad')}` ? 'underline' : ''
                      } flex items-center underline-offset-8 hover:underline`}
                    >
                      <span className='mr-2 text-brand'>
                        <MdAdd />
                      </span>
                      {t('newAd')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={`/${path('profile')}/${path('bids')}/${path('allbids')}`}
                      onClick={closeMenu}
                      className={`${
                        pathname ===
                        `/${path('profile')}/${path('bids')}/${path('allbids')}`
                          ? 'underline'
                          : ''
                      } flex items-center underline-offset-8 hover:underline`}
                    >
                      <span
                        className='mr-2 text-brand'
                        style={{ transform: 'rotateY(180deg)' }}
                      >
                        <MdGavel />
                      </span>
                      {t('yourActiveBids')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={`/${path('profile')}/${path('favourites')}`}
                      onClick={closeMenu}
                      className={`${
                        pathname === `/${path('profile')}/${path('favourites')}`
                          ? 'underline'
                          : ''
                      } flex items-center underline-offset-8 hover:underline`}
                    >
                      <span className='mr-2 text-brand'>
                        <MdStar />
                      </span>
                      {t('favourites')}
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={() => {
                        closeMenu();
                        editParam('modal', 'account');
                      }}
                      className='flex items-center underline-offset-8 hover:underline'
                    >
                      <span className='mr-2 text-brand'>
                        {customer.type === customerTypes.company ? (
                          <BiSolidFactory />
                        ) : (
                          <MdPerson />
                        )}
                      </span>
                      <span>
                        <span className='pr-2 lg:hidden'>{t('customer')}:</span>
                        {customer.name
                          ? customer.name
                          : user.firstName + ' ' + user.lastName}
                      </span>
                    </button>
                  </li>
                  <li className='lg:hidden'>
                    <Link
                      href={`/${path('profile')}`}
                      onClick={closeMenu}
                      className='flex items-center underline-offset-8 hover:underline'
                    >
                      <span className='mr-2 text-brand'>
                        <BiSolidUserDetail />
                      </span>
                      {t('profile')}
                    </Link>
                  </li>
                  <li className='lg:hidden'>
                    <button
                      onClick={() => {
                        logoutHandler();
                        closeMenu();
                      }}
                      className='flex items-center underline-offset-8 hover:underline'
                    >
                      <span className='mr-2 text-brand'>
                        <MdLogout />
                      </span>
                      {t('logout')}
                    </button>
                  </li>
                </>
              )}
            </ul>
          </nav>
        </section>
        <div className={`${menuOpen ? 'block' : 'hidden'} order-6 lg:block`}>
          <CategorySubNavbar categorySlug={null} onClick={closeMenu} />
        </div>
      </header>
      {/* TODO: optimize loading by making it conditional */}
      <InfoConfirmationModal />
      {searchParams.get('modal') && (
        <ErrorBoundary
          fallback={
            <Modal>
              <Fallback layout='sm' />
            </Modal>
          }
        >
          <ComplaintFormModal />
          <SellFormModal />
          {getOption('SHOW_USER_FUNCTIONS') && (
            <>
              <LoginModal />
              <ExtendedRegistrationModal />
              <ForgottenModal />
              <ResetPasswordModal />
              <FirstLoginModal />
              <AccountModal />
              <CompanyRegistrationModal />
            </>
          )}
        </ErrorBoundary>
      )}
    </>
  );
}
